import Keycloak from 'keycloak-js';
import { findValueByKeyPath, omitKeys } from './utils'; // Make sure these are defined

class KeycloakManager {
  constructor(config) {
    const { groupsKey, rolesKey, ...keycloakConfig } = config;

    this.keycloak = new Keycloak(keycloakConfig);
    this.groupsKey = groupsKey;
    this.rolesKey = rolesKey;
    this.authenticatePromise = null;
  }

  async authenticate() {
    if (this.isAuthenticated) {
      return true;
    }

    if (this.authenticatePromise) {
      return this.authenticatePromise;
    }

    this.authenticatePromise = new Promise((resolve, reject) => {
      const storedTokens = this.getStoredTokens();

      this.keycloak
        .init({
          checkLoginIframe: false,
          onLoad: 'login-required',
          ...storedTokens,
        })
        .success((authenticated) => {
          if (!authenticated) {
            reject(new Error('User could not be authenticated'));
          }
          this.storeTokens();
          resolve(authenticated);
        })
        .error((err) => {
          this.clearStoredTokens();
          reject(err);
        });
    });

    return this.authenticatePromise.finally(() => {
      this.authenticatePromise = null;
    });
  }

  get isAuthenticated() {
    return this.keycloak.authenticated === true;
  }

  get token() {
    return this.keycloak.token;
  }

  async updateToken() {
    return new Promise((resolve, reject) => {
      this.keycloak
        .updateToken()
        .success(() => resolve(this.token))
        .error((err) => reject(err));
    });
  }

  login(options = {}) {
    this.keycloak.login(options);
  }

  logout() {
    this.clearStoredTokens();
    if (typeof this.keycloak.clearToken === 'function') {
      this.keycloak.clearToken(); // only present in older versions
    }
    this.keycloak.logout();
  }

  isInGroup(groups) {
    return groups.some((group) =>
      this.userDoesMatchGroup(new RegExp(`^${group}$`, 'i'))
    );
  }

  hasRole(roles) {
    const userRoles = this.getUserRoles();
    if (!userRoles) return false;

    return roles.some((role) =>
      userRoles.some((ur) => role.toLowerCase() === ur.toLowerCase())
    );
  }

  getUserInfo() {
    if (!this.keycloak.tokenParsed) return undefined;

    return omitKeys(this.keycloak.tokenParsed, [
      'jti', 'exp', 'nbf', 'iat', 'iss', 'aud',
      'typ', 'azp', 'nonce', 'auth_time', 'session_state',
      'acr', 'allowed-origins', 'realm_access',
      'resource_access', 'scope',
    ]);
  }

  getUserName() {
   return this.keycloak.tokenParsed && this.keycloak.tokenParsed.preferred_username;

  }

  getUserRoles() {
    if (!this.keycloak.tokenParsed || !this.rolesKey) return [];
    const roles = findValueByKeyPath(this.keycloak.tokenParsed, this.rolesKey);
    return Array.isArray(roles) ? roles : [];
  }

  getUserGroups() {
    if (!this.keycloak.tokenParsed || !this.groupsKey) return [];
    const groups = findValueByKeyPath(this.keycloak.tokenParsed, this.groupsKey);
    return Array.isArray(groups) ? groups : [];
  }

  userDoesMatchGroup(groupMatcher) {
    const groups = this.getUserGroups();
return groups && groups.some(function (group) {
  return groupMatcher.test(group);
});
  }

  storeTokens() {
    localStorage.setItem(
      'tokens',
      JSON.stringify({
        token: this.keycloak.token,
        idToken: this.keycloak.idToken,
        refreshToken: this.keycloak.refreshToken,
      })
    );
  }

  getStoredTokens() {
    try {
      return JSON.parse(localStorage.getItem('tokens') || 'null') || undefined;
    } catch {
      return undefined;
    }
  }

  clearStoredTokens() {
    localStorage.removeItem('tokens');
  }
}

export default KeycloakManager;
